import { useEffect, useState, useRef, useCallback, useMemo, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import HomePage from "./pages/HomePage";
import AuthCallback from "./pages/AuthCallback";
import AuthPage from "./pages/AuthPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import DriverDashboard from "./pages/DriverDashboard";
import DriverLoginPage from "./pages/DriverLoginPage";
import PaymentPage from "./pages/PaymentPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import QuoteConfirmation from "./pages/QuoteConfirmation";
import RoutesPage from "./pages/RoutesPage";
import RouteLandingPage from "./pages/RouteLandingPage";
import RouteCategoryPage from "./pages/RouteCategoryPage";
import VeniceHotelsPage from "./pages/VeniceHotelsPage";
import VeniceHotelDetailPage from "./pages/VeniceHotelDetailPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import LegalPage from "./pages/LegalPage";
import FAQPage from "./pages/FAQPage";
import WhyChooseUsPage from "./pages/WhyChooseUsPage";
import WhatsAppButton from "./components/WhatsAppButton";
import CookieConsent from "./components/CookieConsent";
import ThemeProvider from "./components/ThemeProvider";
import ErrorBoundary from "./components/ErrorBoundary";
import { installGoogleAnalytics, installGoogleAds } from "./lib/analytics";
import { isCategoryAllowed } from "./lib/cookieConsent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Public site config (GA, Google review URL, WhatsApp number, ...)
export const PublicConfigContext = createContext({ google_analytics_id: null, google_business_url: null, whatsapp_number: null, google_ads_conversion_id: null, google_ads_conversion_label: null });
export const usePublicConfig = () => useContext(PublicConfigContext);

const PublicConfigProvider = ({ children }) => {
  const [config, setConfig] = useState({ google_analytics_id: null, google_business_url: null, whatsapp_number: null, google_ads_conversion_id: null, google_ads_conversion_label: null });
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/public-config`)
      .then((res) => {
        if (!alive) return;
        setConfig(res.data);
        // Only install GA if the user has consented to analytics cookies
        if (res.data.google_analytics_id && isCategoryAllowed("analytics")) {
          installGoogleAnalytics(res.data.google_analytics_id);
        }
        // Google Ads conversion tag needs Marketing consent
        if (res.data.google_ads_conversion_id && isCategoryAllowed("marketing")) {
          installGoogleAds(res.data.google_ads_conversion_id);
        }
      })
      .catch((err) => { console.warn("public-config failed", err); });
    return () => { alive = false; };
  }, []);
  // React to consent changes (user accepts analytics later → install GA on the fly)
  useEffect(() => {
    const handler = () => {
      if (config.google_analytics_id && isCategoryAllowed("analytics")) {
        installGoogleAnalytics(config.google_analytics_id);
      }
      if (config.google_ads_conversion_id && isCategoryAllowed("marketing")) {
        installGoogleAds(config.google_ads_conversion_id);
      }
    };
    window.addEventListener("tnm-cookie-consent-change", handler);
    return () => window.removeEventListener("tnm-cookie-consent-change", handler);
  }, [config.google_analytics_id, config.google_ads_conversion_id]);
  return <PublicConfigContext.Provider value={config}>{children}</PublicConfigContext.Provider>;
};

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set up axios interceptor to add Authorization header as fallback
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const token = localStorage.getItem("session_token");
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptor);
  }, []);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("session_token");
      const response = await axios.get(`${API}/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      // Only purge the token on explicit 401 (invalid/expired session) — not on
      // network/CORS/other errors which would otherwise lock out a valid user.
      if (error?.response?.status === 401 && !localStorage.getItem("session_token")) {
        // No token + 401 = nothing to clear.
      } else if (error?.response?.status === 401) {
        localStorage.removeItem("session_token");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginWithToken = async (sessionToken, userData) => {
    if (sessionToken) localStorage.setItem("session_token", sessionToken);
    if (userData) {
      setUser(userData);
    } else {
      await checkAuth();
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
      setUser(null);
      localStorage.removeItem("session_token");
      toast.success("Logged out successfully");
    } catch (error) {
      setUser(null);
      localStorage.removeItem("session_token");
    }
  };

  return (
    <AuthContext.Provider value={useMemo(
      () => ({ user, setUser, loading, login, loginWithToken, logout, checkAuth }),
      [user, loading, login, loginWithToken, logout, checkAuth]
    )}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (adminOnly && user.role !== "admin" && user.role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Scroll to top on every path change — react-router doesn't do this by
  // default and it caused mobile users landing on route/hotel pages to see the
  // page mid-scroll (e.g. right in the middle of the price panel).
  useEffect(() => {
    // Skip when a hash anchor is present (e.g. #tailor-made) — the browser
    // handles that natively.
    if (location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/routes" element={<RoutesPage />} />
      <Route path="/routes/category/:slug" element={<RouteCategoryPage />} />
      <Route path="/hotels-in-venice" element={<VeniceHotelsPage />} />
      <Route path="/hotels-in-venice/:slug" element={<VeniceHotelDetailPage />} />
      <Route path="/route/:slug" element={<RouteLandingPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/legal/:slug" element={<LegalPage />} />
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/why-choose-us" element={<WhyChooseUsPage />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/register" element={<AuthPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/quote-confirmation/:quoteId" element={<QuoteConfirmation />} />
      <Route
        path="/dashboard"
        element={<Dashboard />}
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/driver/login" element={<DriverLoginPage />} />
      <Route
        path="/driver"
        element={
          <ProtectedRoute>
            <DriverDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="/pay/success" element={<PaymentSuccessPage />} />
      <Route path="/pay/:quoteId" element={<PaymentPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <ThemeProvider>
            <PublicConfigProvider>
              <AuthProvider>
                <AppRouter />
                <WhatsAppButton />
                <CookieConsent />
                <Toaster 
                  position="top-right" 
                  theme="dark"
                  toastOptions={{
                    style: {
                      background: '#1F1F1F',
                      border: '1px solid #333333',
                      color: '#FFFFFF',
                    },
                  }}
                />
              </AuthProvider>
            </PublicConfigProvider>
          </ThemeProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
export { API, BACKEND_URL };
